import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  ClipboardCheck, ArrowRight, ArrowLeft, Phone, AlertTriangle, 
  CheckCircle, Activity, Building2, Stethoscope
} from "lucide-react";

const QUICK_QUESTIONS = [
  {
    id: "prognosis",
    question: "Does the patient have a serious illness with a prognosis of 6 months or less if the disease runs its normal course?",
    weight: 30
  },
  {
    id: "functional_decline",
    question: "Has the patient shown significant decline in ability to perform daily activities (bathing, dressing, eating)?",
    weight: 25
  },
  {
    id: "hospitalizations",
    question: "Has the patient had 2 or more hospitalizations in the past 6 months?",
    weight: 20
  },
  {
    id: "weight_loss",
    question: "Has the patient experienced unintentional weight loss of more than 10% in the past 6 months?",
    weight: 15
  },
  {
    id: "comfort_care",
    question: "Has the patient or family expressed interest in comfort-focused care over aggressive treatment?",
    weight: 10
  }
];

export default function QuickEligibilityWidget() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const calculateScore = () => {
    let score = 0;
    QUICK_QUESTIONS.forEach(q => {
      if (answers[q.id] === "yes") {
        score += q.weight;
      }
    });
    return score;
  };

  const getResult = () => {
    const score = calculateScore();
    if (score >= 70) {
      return {
        level: "high",
        title: "High Likelihood of Hospice Eligibility",
        message: "Based on your responses, this patient may benefit from a hospice consultation. We recommend contacting Goodwin Hospice for a comprehensive evaluation.",
        color: "text-red-600 bg-red-50",
        icon: AlertTriangle
      };
    } else if (score >= 40) {
      return {
        level: "moderate",
        title: "Moderate Likelihood of Hospice Eligibility",
        message: "This patient may be approaching hospice eligibility. Consider scheduling a goals of care conversation and re-assessing in the near future.",
        color: "text-yellow-600 bg-yellow-50",
        icon: Activity
      };
    } else {
      return {
        level: "low",
        title: "May Not Currently Meet Criteria",
        message: "Based on your responses, this patient may not currently meet hospice criteria. However, early palliative care consultation can still be beneficial.",
        color: "text-green-600 bg-green-50",
        icon: CheckCircle
      };
    }
  };

  const handleNext = () => {
    if (currentQuestion < QUICK_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const reset = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
  };

  const progress = ((currentQuestion + 1) / QUICK_QUESTIONS.length) * 100;
  const question = QUICK_QUESTIONS[currentQuestion];
  const canProceed = answers[question?.id] !== undefined;

  if (!isOpen) {
    return (
      <Card 
        className="cursor-pointer hover:shadow-lg transition-all border-primary/20 hover:border-primary/40"
        onClick={() => setIsOpen(true)}
        data-testid="quick-eligibility-widget"
      >
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <ClipboardCheck className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Quick Eligibility Check</h3>
              <p className="text-sm text-muted-foreground">
                5 questions • 2 min • No registration required
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showResult) {
    const result = getResult();
    const ResultIcon = result.icon;
    
    return (
      <Card className="border-2 border-primary/20">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Assessment Result
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className={`p-6 rounded-xl ${result.color}`}>
            <div className="flex items-start gap-4">
              <ResultIcon className="w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2">{result.title}</h3>
                <p>{result.message}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-3">
            <p className="text-sm font-medium">Ready to take the next step?</p>
            
            <Button 
              size="lg" 
              className="w-full"
              onClick={() => window.open("tel:703-578-7108")}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Goodwin Hospice: 703-578-7108
            </Button>
            
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate("/facility")}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Facility Portal
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/physician")}
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                Physician Portal
              </Button>
            </div>
            
            <Button 
              variant="secondary"
              onClick={() => navigate("/facility/criteria")}
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Full Disease-Specific Assessment
            </Button>
          </div>

          <Button variant="ghost" className="w-full" onClick={reset}>
            Start Over
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            Quick Eligibility Check
          </CardTitle>
          <Badge variant="secondary">
            {currentQuestion + 1} of {QUICK_QUESTIONS.length}
          </Badge>
        </div>
        <Progress value={progress} className="h-2 mt-2" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="min-h-[100px]">
          <p className="text-lg font-medium mb-4">{question.question}</p>
          
          <RadioGroup
            value={answers[question.id]}
            onValueChange={(v) => handleAnswer(question.id, v)}
            className="space-y-3"
          >
            <div 
              className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
              onClick={() => handleAnswer(question.id, "yes")}
            >
              <RadioGroupItem value="yes" id={`${question.id}-yes`} />
              <Label htmlFor={`${question.id}-yes`} className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div 
              className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
              onClick={() => handleAnswer(question.id, "no")}
            >
              <RadioGroupItem value="no" id={`${question.id}-no`} />
              <Label htmlFor={`${question.id}-no`} className="cursor-pointer flex-1">No</Label>
            </div>
            <div 
              className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
              onClick={() => handleAnswer(question.id, "unsure")}
            >
              <RadioGroupItem value="unsure" id={`${question.id}-unsure`} />
              <Label htmlFor={`${question.id}-unsure`} className="cursor-pointer flex-1">Unsure</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => currentQuestion > 0 ? setCurrentQuestion(currentQuestion - 1) : setIsOpen(false)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {currentQuestion === 0 ? "Cancel" : "Previous"}
          </Button>
          
          <Button onClick={handleNext} disabled={!canProceed}>
            {currentQuestion < QUICK_QUESTIONS.length - 1 ? "Next" : "See Results"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
